// Pacific-time (PT) dates and the one trailing-12-month window every EVE figure uses
// (FIG-429 · UC-252). Shared by the client and the Deno edge functions, so it has
// NO imports (edge functions import it as '../../../src/shared/ptDates.ts').
//
// The contract ("as of X"):
//   - X is a calendar date in America/Los_Angeles and means ALL of X in PT.
//   - The window is [ (X + 1 day) - 12 months 00:00 PT , (X + 1 day) 00:00 PT ).
//   - With no date, X is yesterday in PT, so the window ends at midnight PT today.
//   - "Today" is never selectable: the latest date is yesterday in PT.
// Example: on 26 Oct 2026, X = 25 Oct 2026 and the window is
// 26 Oct 2025 00:00 PT to 26 Oct 2026 00:00 PT.

export const PT_TIME_ZONE = 'America/Los_Angeles';

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** 'YYYY-MM-DD' for the calendar day of `at` in `timeZone` (default: Pacific time). */
export function dateInTimeZone(at: Date, timeZone: string = PT_TIME_ZONE): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Adds whole days to a 'YYYY-MM-DD' date (calendar arithmetic, no time zone). */
export function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

/** Subtracts 12 calendar months from 'YYYY-MM-DD', clamping the day (29 Feb -> 28 Feb). */
export function subtractTwelveMonths(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 - 12, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  const month = String(target.getUTCMonth() + 1).padStart(2, '0');
  const day = String(Math.min(d, lastDay)).padStart(2, '0');
  return `${target.getUTCFullYear()}-${month}-${day}`;
}

/** Today's calendar date in PT. */
export function ptToday(now: Date = new Date()): string {
  return dateInTimeZone(now);
}

/** Yesterday's calendar date in PT: the latest as-of date, and the default one. */
export function ptYesterday(now: Date = new Date()): string {
  return addDays(ptToday(now), -1);
}

/** The hour (0-23) on the PT clock; for a DST-safe "run only at this PT hour" gate. */
export function ptHour(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: PT_TIME_ZONE, hour: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  return Number(parts.find((p) => p.type === 'hour')?.value);
}

/**
 * The label (PT cut-off date) of an inventory snapshot taken at `now`: the PT midnight it is
 * taken next to. The schedule runs just after midnight PT (label = today), but a run in the last
 * two hours of a PT day (the old '59 6 * * *' UTC cron: 23:59 PDT / 22:59 PST) is a snapshot of
 * the stock at the coming midnight, so it gets tomorrow's label, as it did before (FIG-429 · UC-253).
 */
export function inventorySnapshotLabel(now: Date = new Date()): string {
  return ptHour(now) >= 22 ? addDays(ptToday(now), 1) : ptToday(now);
}

/**
 * The UTC instant of midnight PT at the start of `ymd`, as an ISO timestamp
 * ('YYYY-MM-DDTHH:00:00Z': 07:00 or 08:00 UTC depending on daylight saving).
 */
export function ptMidnightUtc(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  for (const hour of [7, 8]) {
    const candidate = new Date(Date.UTC(y, m - 1, d, hour));
    if (ptHour(candidate) === 0 && dateInTimeZone(candidate) === ymd) {
      return `${ymd}T${String(hour).padStart(2, '0')}:00:00Z`;
    }
  }
  return `${ymd}T08:00:00Z`;
}

/** True for a plain 'YYYY-MM-DD' string. */
export function isYmd(value: unknown): value is string {
  return typeof value === 'string' && YMD.test(value);
}

/** The leading 'YYYY-MM-DD' of a date or ISO timestamp string, else undefined. */
export function ymdOf(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const head = value.trim().slice(0, 10);
  return YMD.test(head) ? head : undefined;
}

export interface Trailing12Window {
  /** The as-of date: the last day in the window (all of it, in PT). */
  asOf: string;
  /** First day in the window (00:00 PT at the start of this date). */
  startDate: string;
  /** Exclusive end: 00:00 PT at the start of this date (asOf + 1 day). */
  endDate: string;
  /** startDate 00:00 PT as a UTC instant. */
  startInstant: string;
  /** endDate 00:00 PT as a UTC instant (exclusive). */
  endInstant: string;
}

/** The trailing-12-month window "as of" `asOf` (the as-of day is included in full). */
export function trailing12Window(asOf: string): Trailing12Window {
  const endDate = addDays(asOf, 1);
  const startDate = subtractTwelveMonths(endDate);
  return { asOf, startDate, endDate, startInstant: ptMidnightUtc(startDate), endInstant: ptMidnightUtc(endDate) };
}

/** Caps an as-of date at yesterday in PT: today and the future can't be selected. */
export function clampAsOfToYesterday(asOf: string, now: Date = new Date()): string {
  const latest = ptYesterday(now);
  return asOf > latest ? latest : asOf;
}

/** The as-of date a request means: the given one (never later than yesterday PT), or yesterday PT. */
export function effectiveAsOf(asOf: string | null | undefined, now: Date = new Date()): string {
  const value = ymdOf(asOf);
  return value === undefined ? ptYesterday(now) : clampAsOfToYesterday(value, now);
}

/**
 * True when the as-of date is the default one: empty, yesterday in PT, or a later
 * date (which is capped at yesterday), so it reads the default table.
 */
export function isDefaultAsOf(asOf: string | null | undefined, now: Date = new Date()): boolean {
  const value = ymdOf(asOf);
  return value === undefined || value >= ptYesterday(now);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Oct 27, 2026" for '2026-10-27'. */
export function formatYmd(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** The date for a 'YYYY-MM-DD' string as a local-midnight Date (for date pickers). */
export function ymdToLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** The 'YYYY-MM-DD' of a picker's local-midnight Date. */
export function localDateToYmd(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
